import { useContext, useState } from "react";
import { Search, Plus, Layers, Filter } from "lucide-react";
import { InterviewQaProvider, useInterviewQa } from "./modal/InterviewQaContext";
import QuestionCard from "./ui/QuestionCard";
import QuestionForm from "./ui/QuestionForm";
import type { InterviewQuestion, QuestionCreate } from "./api/InterviewQaApi";
import { VerifyContext } from "app/VerifyContext";

const DIFFICULTY_FILTER_OPTIONS = [
  { value: "", label: "All difficulties" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "answered", label: "Answered" },
  { value: "unanswered", label: "Unanswered" },
];

function InterviewQaContent() {
  const {
    categories, questions, questionsLoading, categoriesLoading,
    filters, activeCategoryId,
    setActiveCategoryId, setFilters,
    createQuestion, updateQuestion, deleteQuestion,
  } = useInterviewQa();

  const { superAdmin, effectivePermissions } = useContext(VerifyContext);
  const canEdit = superAdmin || !!effectivePermissions["interview_qa"]?.can_edit;

  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<InterviewQuestion | null>(null);

  function openAdd() { setEditingQuestion(null); setShowForm(true); }
  function openEdit(q: InterviewQuestion) { setEditingQuestion(q); setShowForm(true); }

  const totalCount = questions.length;
  const answeredCount = questions.filter((q) => q.status === "answered").length;

  return (
    <div className="flex gap-6 h-full min-h-0">
      {/* Left panel — categories */}
      <div className="w-56 shrink-0 flex flex-col gap-1">
        <p className="text-[11px] text-muted-foreground font-inter font-medium uppercase tracking-wider px-2 mb-1">Categories</p>

        <button
          onClick={() => setActiveCategoryId(null)}
          className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-inter transition-colors ${
            activeCategoryId === null
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
          }`}
        >
          <span className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            All Questions
          </span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${activeCategoryId === null ? "bg-primary-foreground/20" : "bg-muted"}`}>
            {categories.reduce((s, c) => s + (c.question_count ?? 0), 0)}
          </span>
        </button>

        {categoriesLoading && (
          <div className="px-3 py-2 text-xs text-muted-foreground font-inter">Loading…</div>
        )}

        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoryId(cat.id === activeCategoryId ? null : cat.id)}
            className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-inter transition-colors ${
              activeCategoryId === cat.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
            }`}
          >
            <span className="truncate text-left">{cat.name}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full shrink-0 ${activeCategoryId === cat.id ? "bg-primary-foreground/20" : "bg-muted"}`}>
              {cat.question_count ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Right panel — questions */}
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={filters.search ?? ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
              className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="Search questions…"
            />
          </div>

          {/* Difficulty filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <select
              value={filters.difficulty ?? ""}
              onChange={(e) => setFilters({ ...filters, difficulty: e.target.value || undefined })}
              className="bg-surface border border-border rounded-xl pl-8 pr-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary font-inter appearance-none"
            >
              {DIFFICULTY_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <select
            value={filters.status ?? ""}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
            className="bg-surface border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary font-inter appearance-none"
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {canEdit && (
            <button
              onClick={openAdd}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter transition-colors ml-auto"
            >
              <Plus className="w-4 h-4" />
              Add Question
            </button>
          )}
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground font-inter">
          <span>{totalCount} question{totalCount !== 1 ? "s" : ""}</span>
          <span className="text-primary">{answeredCount} answered</span>
          <span className="text-muted-foreground">{totalCount - answeredCount} unanswered</span>
        </div>

        {/* Question list */}
        <div className="flex-1 space-y-3 overflow-y-auto">
          {questionsLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!questionsLoading && questions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <p className="font-inter text-sm">No questions found.</p>
              {canEdit && (
                <button onClick={openAdd} className="mt-3 text-primary hover:text-primary/80 text-sm font-inter underline">
                  Add the first question
                </button>
              )}
            </div>
          )}

          {!questionsLoading && questions.map((q) => (
            <QuestionCard
              key={q.id}
              question={q}
              categories={categories}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={deleteQuestion}
            />
          ))}
        </div>
      </div>

      {/* Question form modal */}
      {showForm && (
        <QuestionForm
          categories={categories}
          initial={editingQuestion}
          defaultCategoryId={activeCategoryId}
          onSave={editingQuestion
            ? (data) => updateQuestion(editingQuestion.id, data)
            : (data) => createQuestion(data as QuestionCreate) // create mode: the form always sends a full payload
          }
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

export default function InterviewQaPage() {
  return (
    <InterviewQaProvider>
      <div className="flex flex-col gap-6 h-full">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">Interview Q&amp;A</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">Knowledge base of interview questions and answers organised by domain.</p>
        </div>
        <InterviewQaContent />
      </div>
    </InterviewQaProvider>
  );
}
